import { Diagnostic } from "../models/diagnostic.js";

/**
 * Checks for React Native project layout and configuration issues.
 * RULES:
 * 1. PACKAGE.JSON MISSING
 * 2. REACT NATIVE NOT DETECTED
 * 3. IOS DIRECTORY MISSING
 * 4. ANDROID DIRECTORY MISSING
 */
export function checkReactNativeDiagnostic(
  packageJsonExists: boolean,
  reactNativeInstalled: boolean,
  iosDirExists: boolean,
  androidDirExists: boolean
): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];

  // 1. PACKAGE.JSON MISSING
  if (!packageJsonExists) {
    diagnostics.push({
      id: "package-json-missing",
      title: "package.json Not Found",
      severity: "error",
      detected: "Could not find package.json in the current or any parent directories.",
      explanation: "A package.json file is required to manage dependencies and identify project configurations. You are likely not running this command in a Node.js project root.",
      symptoms: [
        "Cannot run React Native CLI project-specific commands",
        "Missing node_modules and dependency hooks",
      ],
      fixes: [
        "Change directory (cd) into the root of your React Native project.",
        "Initialize a new React Native project using 'npx @react-native-community/cli@latest init <ProjectName>'.",
      ],
      documentationUrl: "https://reactnative.dev/docs/environment-setup",
    });
    // Return early because other checks require a package.json to proceed
    return diagnostics;
  }

  // 2. REACT NATIVE NOT DETECTED
  if (!reactNativeInstalled) {
    diagnostics.push({
      id: "react-native-missing",
      title: "React Native Not Detected",
      severity: "error",
      detected: "package.json exists, but 'react-native' is missing from dependencies and devDependencies.",
      explanation: "The core React Native dependency is missing. This project cannot build or run as a React Native application.",
      symptoms: [
        "npm/yarn/pnpm run commands for iOS and Android fail to execute",
        "React Native CLI features are unavailable",
      ],
      fixes: [
        "Verify you are in the correct repository folder.",
        "Install the react-native package by running 'npm install react-native'.",
      ],
      documentationUrl: "https://reactnative.dev/docs/environment-setup",
    });
  }

  // 3. IOS DIRECTORY MISSING
  if (!iosDirExists) {
    diagnostics.push({
      id: "ios-dir-missing",
      title: "Missing iOS Directory",
      severity: "warning",
      detected: "The 'ios' directory is missing in the project root.",
      explanation: "The 'ios' directory contains native Xcode project and workspace configurations. Without it, you cannot build a native iOS application locally.",
      symptoms: [
        "Cannot compile or execute iOS native builds using Xcode or CLI",
      ],
      fixes: [
        "If using Expo: This is expected in a managed workflow. Run 'npx expo prebuild' to generate native folders when ejecting or preparing for native builds.",
        "If standard React Native: Re-checkout the directory from version control (e.g. git checkout) or re-run initialization template files.",
      ],
      documentationUrl: "https://reactnative.dev/docs/environment-setup",
    });
  }

  // 4. ANDROID DIRECTORY MISSING
  if (!androidDirExists) {
    diagnostics.push({
      id: "android-dir-missing",
      title: "Missing Android Directory",
      severity: "warning",
      detected: "The 'android' directory is missing in the project root.",
      explanation: "The 'android' directory holds the Gradle build system configurations. Without it, compiling a native Android package (.apk/.aab) is impossible.",
      symptoms: [
        "Cannot compile or execute Android native builds using Gradle or Android Studio",
      ],
      fixes: [
        "If using Expo: This is expected in a managed workflow. Run 'npx expo prebuild' to generate native folders.",
        "If standard React Native: Verify your project's version control, or generate the templates again.",
      ],
      documentationUrl: "https://reactnative.dev/docs/environment-setup",
    });
  }

  return diagnostics;
}
